/* <infinity-slider> — wiederverwendbarer Endlos-Slider-Controller.
   Referenziert Track und Pfeil-Buttons per CSS-Selektor (section-scoped),
   damit bestehende Section-Layouts (Grid-Areas, Header-Navigation) unberührt
   bleiben. Enthält selbst die Fortschritts-Bar.

   <infinity-slider data-track="SEL" data-prev="SEL" data-next="SEL">
     <div class="ifs-progress" data-infinity-progress>
       <div class="ifs-progress__thumb" data-infinity-thumb></div>
       <div class="ifs-progress__thumb" data-infinity-thumb-wrap></div>
     </div>
   </infinity-slider>

   Verhalten (identisch zur Coming-Soon-Section):
   - Track-Items werden 3x gerendert (Original + 2 Klone), Start in der
     mittleren Kopie -> ab Start in beide Richtungen endlos.
   - Nach jedem Scroll-Ende wird die Position unsichtbar um exakt eine
     Set-Breite zurück in die Mitte verschoben.
   - Fortschritts-Balken (ein Item-Anteil breit, min. 10%) wandert 1:1 mit;
     am Set-Ende fließt er über ein Wrap-Segment nahtlos wieder ein. */
if (!customElements.get('infinity-slider')) {
  class InfinitySlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.track = this.dataset.track ? document.querySelector(this.dataset.track) : null;
      this.prevBtn = this.dataset.prev ? document.querySelector(this.dataset.prev) : null;
      this.nextBtn = this.dataset.next ? document.querySelector(this.dataset.next) : null;
      this.progress = this.querySelector('[data-infinity-progress]');
      this.thumb = this.querySelector('[data-infinity-thumb]');
      this.thumbWrap = this.querySelector('[data-infinity-thumb-wrap]');
      if (!this.track) return;
      this.initSlider();
    }

    disconnectedCallback() {
      clearTimeout(this.scrollTimer);
    }

    initSlider() {
      if (this.track.scrollWidth - this.track.clientWidth < 4) {
        if (this.prevBtn) this.prevBtn.hidden = true;
        if (this.nextBtn) this.nextBtn.hidden = true;
        if (this.progress) this.progress.hidden = true;
        // CSS/Bilder evtl. noch nicht fertig geladen — nach window.load erneut messen
        if (!this.retriedInit && document.readyState !== 'complete') {
          this.retriedInit = true;
          window.addEventListener('load', () => this.initSlider(), { once: true });
        }
        return;
      }
      this.initialized = true;
      if (this.prevBtn) this.prevBtn.hidden = false;
      if (this.nextBtn) this.nextBtn.hidden = false;
      if (this.progress) this.progress.hidden = false;

      const items = Array.from(this.track.children);
      this.originalCount = items.length;
      for (let copy = 0; copy < 2; copy += 1) {
        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a, button, input').forEach((focusable) => { focusable.tabIndex = -1; });
          this.track.appendChild(clone);
        });
      }
      // Klone entstehen nach dem ersten Scan von deferred-media.js — ohne neuen Scan
      // bleiben ihre Bilder (data-src) fuer immer auf dem Platzhalter stehen.
      document.dispatchEvent(new CustomEvent('deferred-media:scan', { detail: { root: this.track } }));

      if (this.prevBtn) {
        this.prevBtn.disabled = false;
        this.prevBtn.addEventListener('click', () => this.scrollSlider(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = false;
        this.nextBtn.addEventListener('click', () => this.scrollSlider(1));
      }
      this.measure();
      this.track.addEventListener(
        'scroll',
        () => {
          // Alles im Animation-Frame bündeln — keine Layout-Reads im Scroll-Handler (Jank!)
          if (!this.rafPending) {
            this.rafPending = true;
            requestAnimationFrame(() => {
              this.rafPending = false;
              this.guardBounds();
              this.updateProgress();
            });
          }
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.normalizeScroll(), 120);
        },
        { passive: true }
      );
      this.track.addEventListener('scrollend', () => this.normalizeScroll(), { passive: true });
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          this.measure();
          this.updateProgress();
        }, 150);
      }, { passive: true });
      window.addEventListener('load', () => {
        this.measure();
        this.updateProgress();
      }, { once: true });
      this.track.scrollTo({ left: this.originalCount * this.sliderStep(), behavior: 'instant' });
      this.updateProgress();
    }

    /* Kartenbreite + Gap einmalig messen (Init/Resize/Load) — NICHT pro Scroll-Event */
    measure() {
      const item = this.track.firstElementChild;
      if (!item) {
        this.step = 0;
        return;
      }
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      this.step = item.getBoundingClientRect().width + gap;
    }

    sliderStep() {
      return this.step || 0;
    }

    scrollSlider(direction) {
      const step = this.sliderStep() || this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    normalizeScroll() {
      const setWidth = this.originalCount * this.sliderStep();
      if (setWidth <= 0) return;
      const position = this.track.scrollLeft;
      if (position < setWidth * 0.5) {
        this.track.scrollTo({ left: position + setWidth, behavior: 'instant' });
      } else if (position >= setWidth * 1.5) {
        this.track.scrollTo({ left: position - setWidth, behavior: 'instant' });
      }
    }

    /* Sofort-Teleport MITTEN im Scrollen: Bei schnellem Durchscrollen feuern
       Scroll-Events pausenlos, die Scroll-Ende-Normalisierung kommt nie dran
       und man kracht ans harte Ende der Klon-Strecke. Verlässt die Position
       den sicheren Mittelbereich deutlich, wird sofort um exakt eine
       Set-Breite versetzt — identischer Inhalt, unsichtbar. */
    guardBounds() {
      const setWidth = this.originalCount * this.sliderStep();
      if (setWidth <= 0) return;
      const position = this.track.scrollLeft;
      if (position < setWidth * 0.25) {
        this.track.scrollTo({ left: position + setWidth, behavior: 'instant' });
      } else if (position >= setWidth * 1.75) {
        this.track.scrollTo({ left: position - setWidth, behavior: 'instant' });
      }
    }

    updateProgress() {
      if (!this.progress || !this.thumb) return;
      const setWidth = this.originalCount * this.sliderStep();
      if (setWidth <= 0) return;
      const fraction = ((this.track.scrollLeft % setWidth) + setWidth) % setWidth / setWidth;
      const thumbPercent = Math.max(100 / this.originalCount, 10);
      let left = fraction * 100;
      if (left < 1 || left > 99) left = 0;
      const overflow = left + thumbPercent - 100;
      this.thumb.style.width = `${Math.min(thumbPercent, 100 - left)}%`;
      this.thumb.style.left = `${left}%`;
      if (this.thumbWrap) {
        if (overflow > 0) {
          this.thumbWrap.style.display = 'block';
          this.thumbWrap.style.left = '0%';
          this.thumbWrap.style.width = `${overflow}%`;
        } else {
          this.thumbWrap.style.display = 'none';
        }
      }
    }
  }
  customElements.define('infinity-slider', InfinitySlider);
}
